import {
    afterNextRender,
    AfterRenderRef,
    ComponentRef,
    Directive,
    effect,
    EmbeddedViewRef,
    inject,
    Injector,
    input,
    InputSignal,
    OnDestroy,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';

/**
 * components
 */
import { EmptyOverlayComponent } from './empty-overlay.component';

/**
 * Renders the projected content inside an {@link EmptyOverlayComponent} while
 * `appEmptyOverlay` is true, and directly at the anchor — with the overlay
 * destroyed rather than merely hidden — while it is false.
 *
 * The content itself is built once and kept across every toggle: only the
 * wrapper comes and goes, so whatever lives inside (a chart, a table) is never
 * re-initialised by the data arriving.
 *
 * @example
 * <ng-template [appEmptyOverlay]="isPlaceholder" [appEmptyOverlayMessageKey]="KEY()">
 *     <app-power-metrics-overview [data]="report()" />
 * </ng-template>
 *
 * @example
 * <!-- microsyntax form -->
 * <app-power-metrics-overview
 *     *appEmptyOverlay="isPlaceholder; blur: '2px'"
 *     [data]="report()"
 * />
 */
@Directive({
    selector: '[appEmptyOverlay]',
})
export class EmptyOverlayDirective implements OnDestroy {

    /** The content to wrap, as handed over by the structural directive syntax. */
    private readonly _templateRef: TemplateRef<void> = inject(TemplateRef);

    /** Holds both the content view and the overlay, and marks their place in the DOM. */
    private readonly _viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

    /** Moves the content's nodes in and out of the overlay without rebuilding them. */
    private readonly _renderer: Renderer2 = inject(Renderer2);

    /** Scopes the render hook below, so a destroyed directive drops its pending move. */
    private readonly _injector: Injector = inject(Injector);

    /**
     * The content view — created on the first toggle and kept for the lifetime
     * of the directive, so no later toggle rebuilds what is inside it.
     */
    private _viewRef: EmbeddedViewRef<void> | null = null;

    /** The overlay, while one is mounted. Null whenever the content is unwrapped. */
    private _overlayRef: ComponentRef<EmptyOverlayComponent> | null = null;

    /** The move waiting on the next render, while one is queued. */
    private _pendingMount: AfterRenderRef | null = null;

    /** Wraps the content in the overlay while true, and unwraps it while false. */
    public readonly isEmpty: InputSignal<boolean> = input<boolean>(false, { alias: 'appEmptyOverlay' });

    /** Translation key handed to the overlay, when one is mounted. */
    public readonly messageKey: InputSignal<string> = input<string>('GENERAL.NO_DATA', { alias: 'appEmptyOverlayMessageKey' });

    /** Blur radius handed to the overlay, when one is mounted. */
    public readonly blur: InputSignal<string> = input<string>('1.8px', { alias: 'appEmptyOverlayBlur' });

    /** Follows the inputs: mounts and tears down the overlay, and keeps it fed. */
    constructor() {

        effect(() => {

            // read all three up front, so the effect tracks them whichever branch it takes
            const isEmpty = this.isEmpty();
            const messageKey = this.messageKey();
            const blur = this.blur();

            this._ensureContentView();

            if (!isEmpty) {
                this._unwrap();
                return;
            }

            this._wrap(messageKey, blur);

        });

    }

    /** Builds the content view once, on the first pass through the effect. */
    private _ensureContentView(): void {

        // void
        if (this._viewRef != null) return;

        this._viewRef = this._viewContainerRef.createEmbeddedView(this._templateRef);
    }

    /**
     * Mounts the overlay around the content.
     *
     * The content's nodes are carried into the overlay rather than handed over
     * as `projectableNodes`: that option only fills the slot with nodes given at
     * creation, and will not adopt a view the container already holds — the
     * overlay would come up empty and the content would stay beside it.
     *
     * The view itself is left in this container, which is what keeps change
     * detection reaching it once its nodes have moved.
     *
     * @param messageKey - Translation key for the overlay's message.
     * @param blur - Blur radius for the content beneath it.
     */
    private _wrap(messageKey: string, blur: string): void {

        // void
        if (this._viewRef == null) return;

        // already mounted — keep it fed, and leave the nodes where they are
        if (this._overlayRef != null) {
            this._overlayRef.setInput('messageKey', messageKey);
            this._overlayRef.setInput('blur', blur);
            return;
        }

        const overlayRef = this._viewContainerRef.createComponent(EmptyOverlayComponent);

        overlayRef.setInput('isEmpty', true);
        overlayRef.setInput('messageKey', messageKey);
        overlayRef.setInput('blur', blur);

        this._overlayRef = overlayRef;
        this._scheduleMount(overlayRef);

    }

    /**
     * Carries the content into the overlay once the DOM has settled.
     *
     * The move waits for the render that follows rather than running inside the
     * effect, so it never competes with Angular's own insertion of the views this
     * container has just been given.
     *
     * `afterNextRender` and not a bare microtask: it is tied to the injector, so a
     * destroyed directive drops it, and the guard below covers the rest — by the
     * time it runs the overlay may already have been torn down, and appending into
     * a dead one would take the content out of the page for good.
     *
     * @param overlayRef - The overlay this move was queued for.
     */
    private _scheduleMount(overlayRef: ComponentRef<EmptyOverlayComponent>): void {

        this._pendingMount?.destroy();

        this._pendingMount = afterNextRender({

            write: () => {

                this._pendingMount = null;

                // void — unwrapped, or wrapped again by a later overlay, since
                if (this._overlayRef !== overlayRef || this._viewRef == null) return;

                // reachable without a change detection pass of its own: createComponent
                // runs the overlay's template in creation mode, which resolves its queries
                const slot: HTMLElement = overlayRef.instance.contentHost().nativeElement;

                for (const node of this._viewRef.rootNodes) {
                    this._renderer.appendChild(slot, node);
                }

            },

        }, { injector: this._injector });

    }

    /**
     * Destroys the overlay and leaves the content where the overlay stood.
     *
     * The nodes are carried out before the overlay goes, because they are its
     * children in the DOM by then and destroying it would take them along.
     */
    private _unwrap(): void {

        this._pendingMount?.destroy();
        this._pendingMount = null;

        // void
        if (this._overlayRef == null) return;

        const host: Node = this._overlayRef.location.nativeElement;
        const parent: Node | null = this._renderer.parentNode(host);

        if (parent != null && this._viewRef != null) {
            for (const node of this._viewRef.rootNodes) {
                this._renderer.insertBefore(parent, node, host);
            }
        }

        this._overlayRef.destroy();
        this._overlayRef = null;

    }

    /**
     * Tears both refs down.
     *
     * The overlay goes first: it owns the content's nodes while it is mounted,
     * and destroying the content view underneath it would leave it holding
     * nodes that no view accounts for.
     */
    public ngOnDestroy(): void {

        this._pendingMount?.destroy();
        this._pendingMount = null;

        this._overlayRef?.destroy();
        this._overlayRef = null;

        this._viewRef?.destroy();
        this._viewRef = null;

    }    

}
